import { ConflictType, type Finding } from '@consigli/types';
import clsx from 'clsx';
import { type FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type MilestoneStatus,
  STATUS_STYLES,
} from '@/pages/insight/dashboard/rfp/rfp-timeline-types';
import {
  parseFlexibleDate,
  parseMilestonePayload,
} from '@/pages/insight/dashboard/rfp/rfp-timeline-utils';

import {
  AddContextMilestoneRow,
  describeMilestoneWhen,
  formatMilestoneWhen,
  MILESTONE_TABLE_COLUMNS,
} from './add-context-milestone-row';
import { AddContextPanel } from './add-context-panel';
import { useRfpFindingsList } from './use-rfp-findings-list';

type StatusFilter = 'all' | MilestoneStatus;

const STATUS_FILTERS: StatusFilter[] = ['all', 'active', 'upcoming', 'past'];

const milestoneSearchText = (finding: Finding): string => {
  const payload = parseMilestonePayload(finding);
  return payload ? `${payload.milestone} ${formatMilestoneWhen(payload.dateStr)}` : '';
};

const milestoneStatus = (finding: Finding, today: Date): MilestoneStatus | null => {
  const payload = parseMilestonePayload(finding);
  return payload ? describeMilestoneWhen(payload.dateStr, today).status : null;
};

// Earliest first, and a date the timeline cannot read ("TBC") after every dated one.
const milestoneSortKey = (finding: Finding): number => {
  const payload = parseMilestonePayload(finding);
  const date = payload ? parseFlexibleDate(payload.dateStr) : null;
  return date ? date.getTime() : Number.POSITIVE_INFINITY;
};

interface AddContextMilestonesPanelProps {
  onSelectedChange: (findings: Finding[]) => void;
  selected: Finding[];
}

/**
 * The package's milestones as a table in date order, narrowed by the search box
 * and the status filters. Select All covers what both left showing.
 */
export const AddContextMilestonesPanel: FC<AddContextMilestonesPanelProps> = ({
  onSelectedChange,
  selected,
}) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const today = useMemo(() => new Date(), []);
  const list = useRfpFindingsList(ConflictType.MILESTONE, search, milestoneSearchText);

  const sorted = useMemo(
    () => [...list.items].sort((a, b) => milestoneSortKey(a) - milestoneSortKey(b)),
    [list.items],
  );

  const counts = useMemo(() => {
    const result: Record<StatusFilter, number> = {
      all: sorted.length,
      past: 0,
      active: 0,
      upcoming: 0,
    };
    for (const finding of sorted) {
      const status = milestoneStatus(finding, today);
      if (status) result[status] += 1;
    }
    return result;
  }, [sorted, today]);

  const items = useMemo(
    () =>
      statusFilter === 'all'
        ? sorted
        : sorted.filter((finding) => milestoneStatus(finding, today) === statusFilter),
    [sorted, statusFilter, today],
  );

  const searchLabel = t('prop-chat.add-context-search-milestones');
  const filterLabel = (filter: StatusFilter) =>
    filter === 'all'
      ? t('prop-chat.add-context-filter-all')
      : t(`dashboard.rfp-timeline.${filter}`);

  const listHeader = (
    <div
      aria-hidden="true"
      className={clsx(
        MILESTONE_TABLE_COLUMNS,
        'shrink-0 border-b border-solid border-[var(--ds-color-border-primary)] bg-[var(--ds-color-bg-primary)] py-[var(--ds-space-2-5)]',
        'text-[length:var(--ds-typography-paragraph-mini-font-size)] leading-[var(--ds-typography-paragraph-mini-line-height)] text-[var(--ds-color-text-tertiary)]',
      )}
    >
      <span />
      <span>{t('dashboard.rfp-timeline.columns.milestone')}</span>
      <span>{t('dashboard.rfp-timeline.columns.date')}</span>
      <span>{t('dashboard.rfp-timeline.add-milestone.label-time')}</span>
      <span>{t('dashboard.rfp-timeline.columns.status')}</span>
    </div>
  );

  return (
    <AddContextPanel
      emptyMessage={t('prop-chat.add-context-no-milestones')}
      headerEnd={
        <div
          role="group"
          aria-label={t('prop-chat.add-context-filter-status')}
          className="flex flex-wrap items-center gap-[var(--ds-space-1-5)]"
        >
          {STATUS_FILTERS.map((filter) => (
            <button
              key={filter}
              type="button"
              aria-pressed={statusFilter === filter}
              onClick={() => setStatusFilter(filter)}
              className={clsx(
                'flex cursor-pointer items-center gap-[var(--ds-space-1-5)] rounded-full border border-solid border-[var(--ds-color-bg-tertiary)] px-[var(--ds-space-3)] py-[var(--ds-space-1)]',
                'text-[length:var(--ds-typography-paragraph-mini-font-size)] leading-[var(--ds-typography-paragraph-mini-line-height)]',
                'outline-none focus-visible:outline-[length:var(--ds-space-0-5)] focus-visible:outline-offset-[var(--ds-space-0-5)] focus-visible:outline-[var(--ds-color-border-primary)] focus-visible:outline-solid',
                'hover:bg-[var(--ds-color-bg-tertiary-dark)]',
                statusFilter === filter
                  ? 'bg-[var(--ds-color-bg-tertiary)] text-[var(--ds-color-text-primary)]'
                  : 'text-[var(--ds-color-text-secondary)]',
              )}
            >
              {filter !== 'all' && (
                <span
                  aria-hidden="true"
                  className={clsx(
                    'size-[var(--ds-space-1-5)] rounded-full',
                    STATUS_STYLES[filter].dot,
                  )}
                />
              )}
              {filterLabel(filter)}
              <span className="tabular-nums text-[var(--ds-color-text-tertiary)]">
                {counts[filter]}
              </span>
            </button>
          ))}
        </div>
      }
      list={{ ...list, items }}
      onSearchTextChange={setSearch}
      onSelectedChange={onSelectedChange}
      renderItem={(finding, isSelected, onToggle) => (
        <AddContextMilestoneRow
          key={finding.id}
          finding={finding}
          onSelectedChange={onToggle}
          selected={isSelected}
          today={today}
        />
      )}
      searchLabel={searchLabel}
      searchText={search}
      selected={selected}
      listHeader={listHeader}
    />
  );
};
